import { gql } from 'graphql-request';
import { AxiosRequestConfig } from 'axios';
import { Nullable, UserWithToken, WithData } from './api.types';
import { getGraphQLQueryClient } from './query-client';
import axiosClient from './request-client';

// Vinculación de la cuenta de Discord del usuario (OAuth) para poder taggearlo.
// Los endpoints REST viven en la API (/discord-link/*).

// Server de Discord del torneo, para las pantallas de invitación. Sólo viene
// si se pide con `params: { tournamentId }` y ese torneo tiene comunidad en el
// server (hoy, el Super YACS); con el mismo param, `getDiscordLinkAuthUrl` pide
// además sumar al jugador al server al vincular.
export interface DiscordCommunity {
  inviteUrl: string;
  inServer: boolean;
  hasRole: boolean;
}

export interface DiscordLinkStatus {
  linked: boolean;
  discordId: string | null;
  community?: Nullable<DiscordCommunity>;
}

export const getDiscordLinkStatus = (config?: AxiosRequestConfig) =>
  axiosClient.get<DiscordLinkStatus>('/discord-link/status', config);

export const getDiscordLinkAuthUrl = (
  returnTo: string,
  config?: AxiosRequestConfig
) =>
  axiosClient.get<{ authUrl: string }>('/discord-link/authorize', {
    ...config,
    params: { ...config?.params, returnTo },
  });

export const unlinkDiscord = (config?: AxiosRequestConfig) =>
  axiosClient.post('/discord-link/unlink', undefined, config);

// Búsqueda de miembros del server por nombre. Sólo la puede llamar un admin, y
// existe para resolver nombre -> snowflake sin pedirle el ID al jugador: el
// `discordId` sólo se escribe por OAuth salvo que lo corrija un admin a mano.
export interface DiscordMember {
  id: string;
  username: string;
  displayName: Nullable<string>;
  avatarUrl: Nullable<string>;
}

export const searchDiscordMembers = async (
  user: Nullable<UserWithToken>,
  query: string
) =>
  getGraphQLQueryClient(user).request<WithData<DiscordMember[]>>(
    gql`
      query discordMembers($query: String!) {
        data: discordMembers(query: $query) {
          id
          username
          displayName
          avatarUrl
        }
      }
    `,
    { query }
  );

// Canales del server de Discord (texto, anuncios y categorías), para elegir
// dónde postea el bot. Sólo admins.
export interface DiscordChannel {
  id: string;
  name: string;
  parentId: Nullable<string>;
  type: 'channel' | 'category';
}

export const getDiscordChannels = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<DiscordChannel[]>>(
    gql`
      query discordChannels {
        data: channels {
          id
          name
          parentId
          type
        }
      }
    `
  );
